import {DEFAULT_PAGE_SIZE} from '../../constants';
import {tableHelpers} from './table.helpers';

export const tabHelpers = {
  handleOnPaginate: ({view, activePage, tableData, setTableData, loadDataFunc, single = false}) => {
    const viewTableData = single ? tableData : tableData[view];
    const {page: currentPage, loadedPages} = viewTableData;
    if (activePage > currentPage && loadedPages < activePage) {
      let query = {};
      if (viewTableData.pagination && viewTableData.pagination.cursor) query = {cursor: viewTableData.pagination.cursor};
      loadDataFunc({view, query});
    } else {
      setTableData(single ? {
        ...viewTableData,
        page: activePage
      } : {
        ...tableData,
        [view]: {
          ...viewTableData,
          page: activePage
        }
      });
    }
  },
  handleOnFilter: ({view, filterQuery, loadDataFunc, queries = undefined, setQueries = undefined, single = false}) => {
    loadDataFunc({
      initial: true,
      view,
      query: filterQuery
    });
    if (queries && setQueries) {
      const viewQueries = single ? queries : queries[view];
      setQueries(single ? {
        ...viewQueries,
        ...filterQuery
      } : {
        ...queries,
        [view]: {
          ...viewQueries,
          ...filterQuery
        }
      });
    }
  },
  handleOnTableChange: ({view, sorter, extra, loadDataFunc}) => {
    const {action} = extra;
    if (action === 'sort') {
      loadDataFunc({
        initial: true,
        view,
        query: {
          order_by: tableHelpers.getOrderByValue(sorter)
        }
      })
    }
  },
  getLabel: ({key, total, totalKey, t}) => {
    let totalPropertyName = totalKey || key;
    let value = 0;
    if (total && total.hasOwnProperty(totalPropertyName)) value = total[totalPropertyName];
    return `${t(`viewOptions.${key}`)} (${value})`;
  },
  getLabelWithOptionalTotal: ({key, total, totalKey, t}) => {
    const value = total?.[totalKey || key] || 0;
    const label = t(`viewOptions.${key}`);
    return totalKey ? `${label} (${value})` : label;
  },
  getUpdatedTableData: ({tableData, response, initial, rowsKey = 'results', updateCurrentPage = false}) => {
    const pagination = typeof response?.pagination === 'string'
      ? { cursor: response.pagination }
      : response?.pagination || null;

    const data = response[rowsKey] || [];
    const currentPage = tableData.page || 1;
    let { loadedPages, page, pageCursors = {}, rows = [] } = tableData;

    if (initial) {
      page = 1;
      loadedPages = 1;
      pageCursors = { 1: pagination?.cursor || null };
      rows = data;
    } else {
      if (!updateCurrentPage) {
        page += 1;
        loadedPages = Math.max(loadedPages, page);
        pageCursors = {
          ...pageCursors,
          [page]: pagination?.cursor || null
        }
        rows = [...rows, ...data];
      }
    }

    if (updateCurrentPage) {
      loadedPages = currentPage;
      pageCursors = Object.fromEntries(
        Object.entries(pageCursors).filter(([key]) => parseInt(key, 10) <= currentPage)
      );
      rows = currentPage <= 1 ? [] : rows.slice(0, (currentPage - 1) * DEFAULT_PAGE_SIZE);
      rows = [...rows, ...data];
    }

    return {
      ...tableData,
      loaded: true,
      loadedPages,
      pagination,
      page,
      pageCursors,
      rows
    };
  },
  handleStoreTableData: ({tableData, setTableData, view, response, initial, rowsKey = 'results'}) => {
    const viewTableData = tableData[view];
    let updatedViewTableDate = tabHelpers.getUpdatedTableData({
      tableData: viewTableData,
      response,
      initial,
      rowsKey
    })
    let updatedTableData = {
      ...tableData,
      [view]: updatedViewTableDate
    }

    setTableData(updatedTableData);
    return updatedTableData;
  },
  handleStoreTableDataWithPrev: ({tableData, setTableData, view, response, initial, rowsKey = 'results', updateCurrentPage = false}) => {
    const viewTableData = tableData[view];
    let updatedViewTableDate = tabHelpers.getUpdatedTableData({
      tableData: viewTableData,
      response,
      initial,
      rowsKey,
      updateCurrentPage
    });

    setTableData(prevState => ({...prevState, [view]: updatedViewTableDate}));
  },
  handleStoreStoredTableData: ({tableData, setTableData, view, response, rowsKey, perPagePagination}) => {
    const pagination = response?.pagination || null;
    const data = response[rowsKey] || [];
    const viewTableData = tableData[view];
    let {page} = viewTableData;
    let updatedTableData;

    updatedTableData = {
      ...tableData,
      [view]: {
        loaded: true,
        loadedPages: page,
        pagination,
        page,
        rows: page === 1 ? data : [...tableData[view].rows.slice(0, (page - 1) * perPagePagination), ...data]
      }
    }

    setTableData(updatedTableData);
    return updatedTableData;
  },
  getViewQuery: ({initial, query, queries, view, updateCurrentPage, tableData, single = false}) => ({
    ...(single ? queries ?? {} : queries?.[view] ?? {}),
    ...query,
    ...(initial && { cursor: undefined }),
    ...(updateCurrentPage && tableData && tableData[view]?.page && {
      cursor: tableData[view].pageCursors[tableData[view].page - 1] || undefined
    })
  }),
  getTableOrder: (view, queries) => {
    let tableOrder;
    const query = queries && queries.hasOwnProperty(view) ? queries[view] : undefined;
    let orderBy = query?.order_by || undefined;
    if (orderBy) {
      const desc = orderBy.charAt(0) === '-';
      if (desc) orderBy = orderBy.replace(/^./, '');
      tableOrder = {
        field: orderBy,
        order: desc ? 'descend' : 'ascend'
      }
    }
    return tableOrder;
  },
  getTablePagination: ({handleOnPaginate, tableData, total, totalKey, view, perPagePagination = DEFAULT_PAGE_SIZE, single = false} = {}) => {
    const getTableDataValue = (view, key, defaultValue) => single ? tableData?.[key] : tableData.hasOwnProperty(view) ? tableData[view][key] : defaultValue;
    const data = getTableDataValue(view, 'rows', []);
    const paginationCurrentPage = getTableDataValue(view, 'page', 1);
    const enabledPaginationCursor = Boolean(getTableDataValue(view, 'pagination', null)?.cursor);

    let paginationTotal = total && total.hasOwnProperty(totalKey) ? total[totalKey] : undefined;
    if (paginationTotal === undefined) {
      paginationTotal = data.length;
      if (enabledPaginationCursor) paginationTotal += 1; // Add 1 to total if cursor pagination is enabled
    } else if (enabledPaginationCursor) {
      paginationTotal += 1; // Add 1 to total if cursor pagination is enabled
    }

    if (!enabledPaginationCursor) paginationTotal = data.length;
    return {
      current: paginationCurrentPage,
      disabled: data.length === 0,
      onChange: (page) => handleOnPaginate(view, page),
      pageSize: perPagePagination,
      showSizeChanger: false,
      total: paginationTotal
    }
  }
}
